import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import {
  BarChart3,
  Users,
  FileText,
  BookOpen,
  TrendingUp,
  Eye,
  Flag,
  Clock,
  RefreshCw,
  ArrowLeft,
  MessageSquare,
  CheckCircle,
  XCircle,
  AlertTriangle,
  MessageCircle,
  Award,
  Video,
  Calendar,
  UserCheck,
  XCircle as CancelIcon
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chart Colors
const CHART_COLORS = ['#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B', '#3B82F6', '#EF4444', '#10B981', '#6366F1'];
const GRADE_COLORS = {
  'MEO Class 1': '#8B5CF6',
  'MEO Class 2': '#EC4899',
  'MEO Class 4': '#14B8A6',
  'ETO': '#F59E0B',
  'Master': '#3B82F6',
  'Chief Mate': '#EF4444',
  '2MFG': '#10B981'
};

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('30d');
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [usersByGrade, setUsersByGrade] = useState([]);
  const [usersByMMD, setUsersByMMD] = useState([]);
  const [questionsByGrade, setQuestionsByGrade] = useState([]);
  const [notesByGrade, setNotesByGrade] = useState([]);
  const [topViewedNotes, setTopViewedNotes] = useState([]);
  const [signupsTrend, setSignupsTrend] = useState([]);
  const [activityTrend, setActivityTrend] = useState([]);
  const [flagReasons, setFlagReasons] = useState([]);
  const [contentSummary, setContentSummary] = useState(null);
  
  // Forum analytics states
  const [forumOverview, setForumOverview] = useState(null);
  const [forumByGrade, setForumByGrade] = useState([]);
  const [forumByStatus, setForumByStatus] = useState([]);
  const [forumByFunction, setForumByFunction] = useState([]);
  const [forumByMmd, setForumByMmd] = useState([]);
  const [forumActivityTrend, setForumActivityTrend] = useState([]);
  const [forumTopContributors, setForumTopContributors] = useState([]);
  const [forumResponseTime, setForumResponseTime] = useState(null);

  // Live class analytics states
  const [lcOverview, setLcOverview] = useState(null);
  const [lcActivityTrend, setLcActivityTrend] = useState([]);
  const [lcByGrade, setLcByGrade] = useState([]);
  const [lcByMmd, setLcByMmd] = useState([]);
  const [lcTopHosts, setLcTopHosts] = useState([]);
  const [lcAttendance, setLcAttendance] = useState([]);
  const [lcCancellation, setLcCancellation] = useState(null);

  const fetchAllAnalytics = useCallback(async (showRefreshToast = false) => {
    try {
      if (showRefreshToast) setRefreshing(true);
      else setLoading(true);
      
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch all data in parallel (including forum analytics)
      const [
        overviewRes,
        usersByGradeRes,
        usersByMMDRes,
        questionsByGradeRes,
        notesByGradeRes,
        topViewedRes,
        signupsRes,
        activityRes,
        flagReasonsRes,
        contentSummaryRes,
        // Forum analytics
        forumOverviewRes,
        forumByGradeRes,
        forumByStatusRes,
        forumByFunctionRes,
        forumByMmdRes,
        forumActivityRes,
        forumContributorsRes,
        forumResponseRes,
        // Live class analytics
        lcOverviewRes,
        lcActivityRes,
        lcByGradeRes,
        lcByMmdRes,
        lcTopHostsRes,
        lcAttendanceRes,
        lcCancellationRes
      ] = await Promise.all([
        fetch(`${API_URL}/api/analytics/overview?period=${period}`, { headers }),
        fetch(`${API_URL}/api/analytics/users-by-grade`, { headers }),
        fetch(`${API_URL}/api/analytics/users-by-mmd`, { headers }),
        fetch(`${API_URL}/api/analytics/questions-by-grade`, { headers }),
        fetch(`${API_URL}/api/analytics/notes-by-grade`, { headers }),
        fetch(`${API_URL}/api/analytics/top-viewed-notes?limit=10`, { headers }),
        fetch(`${API_URL}/api/analytics/user-signups-trend?period=${period}`, { headers }),
        fetch(`${API_URL}/api/analytics/activity-trend?period=${period}`, { headers }),
        fetch(`${API_URL}/api/analytics/flag-reasons`, { headers }),
        fetch(`${API_URL}/api/analytics/content-summary`, { headers }),
        // Forum analytics
        fetch(`${API_URL}/api/analytics/forum/overview?period=${period}`, { headers }),
        fetch(`${API_URL}/api/analytics/forum/questions-by-grade`, { headers }),
        fetch(`${API_URL}/api/analytics/forum/questions-by-status`, { headers }),
        fetch(`${API_URL}/api/analytics/forum/questions-by-function`, { headers }),
        fetch(`${API_URL}/api/analytics/forum/questions-by-mmd`, { headers }),
        fetch(`${API_URL}/api/analytics/forum/activity-trend?period=${period}`, { headers }),
        fetch(`${API_URL}/api/analytics/forum/top-contributors?limit=10`, { headers }),
        fetch(`${API_URL}/api/analytics/forum/response-time`, { headers }),
        // Live class analytics — period-driven trend, all others are all-time
        fetch(`${API_URL}/api/analytics/live-classes/overview`, { headers }),
        fetch(`${API_URL}/api/analytics/live-classes/activity-trend?days=${period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : period === '1y' ? 365 : 90}`, { headers }),
        fetch(`${API_URL}/api/analytics/live-classes/by-grade`, { headers }),
        fetch(`${API_URL}/api/analytics/live-classes/by-mmd`, { headers }),
        fetch(`${API_URL}/api/analytics/live-classes/top-hosts?limit=10`, { headers }),
        fetch(`${API_URL}/api/analytics/live-classes/attendance-summary?limit=50`, { headers }),
        fetch(`${API_URL}/api/analytics/live-classes/cancellation-stats`, { headers })
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (usersByGradeRes.ok) setUsersByGrade((await usersByGradeRes.json()).data || []);
      if (usersByMMDRes.ok) setUsersByMMD((await usersByMMDRes.json()).data || []);
      if (questionsByGradeRes.ok) setQuestionsByGrade((await questionsByGradeRes.json()).data || []);
      if (notesByGradeRes.ok) setNotesByGrade((await notesByGradeRes.json()).data || []);
      if (topViewedRes.ok) setTopViewedNotes((await topViewedRes.json()).data || []);
      if (signupsRes.ok) setSignupsTrend((await signupsRes.json()).data || []);
      if (activityRes.ok) setActivityTrend((await activityRes.json()).data || []);
      if (flagReasonsRes.ok) setFlagReasons((await flagReasonsRes.json()).data || []);
      if (contentSummaryRes.ok) setContentSummary(await contentSummaryRes.json());
      
      // Forum analytics
      if (forumOverviewRes.ok) setForumOverview(await forumOverviewRes.json());
      if (forumByGradeRes.ok) setForumByGrade((await forumByGradeRes.json()).data || []);
      if (forumByStatusRes.ok) setForumByStatus((await forumByStatusRes.json()).data || []);
      if (forumByFunctionRes.ok) setForumByFunction((await forumByFunctionRes.json()).data || []);
      if (forumByMmdRes.ok) setForumByMmd((await forumByMmdRes.json()).data || []);
      if (forumActivityRes.ok) setForumActivityTrend((await forumActivityRes.json()).data || []);
      if (forumContributorsRes.ok) setForumTopContributors((await forumContributorsRes.json()).data || []);
      if (forumResponseRes.ok) setForumResponseTime(await forumResponseRes.json());

      // Live class analytics
      if (lcOverviewRes.ok) setLcOverview(await lcOverviewRes.json());
      if (lcActivityRes.ok) setLcActivityTrend((await lcActivityRes.json()).trend || []);
      if (lcByGradeRes.ok) setLcByGrade((await lcByGradeRes.json()).data || []);
      if (lcByMmdRes.ok) setLcByMmd((await lcByMmdRes.json()).data || []);
      if (lcTopHostsRes.ok) setLcTopHosts((await lcTopHostsRes.json()).data || []);
      if (lcAttendanceRes.ok) setLcAttendance((await lcAttendanceRes.json()).data || []);
      if (lcCancellationRes.ok) setLcCancellation(await lcCancellationRes.json());

      if (showRefreshToast) toast.success('Analytics refreshed');
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAllAnalytics();
  }, [fetchAllAnalytics]);

  const handlePeriodChange = (value) => {
    setPeriod(value);
  };

  const handleRefresh = () => {
    fetchAllAnalytics(true);
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-medium text-gray-800">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-[60vh]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
            <p className="text-gray-500">Loading analytics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin')}
              className="text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-7 h-7 text-purple-600" />
                Platform Analytics
              </h1>
              <p className="text-gray-500 text-sm mt-1">Monitor platform activity and engagement</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={handlePeriodChange}>
              <SelectTrigger className="w-[140px]" data-testid="period-selector">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="1y">Last year</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="stat-total-users">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-3xl font-bold text-gray-900">{overview?.total_users || 0}</p>
                  {period !== 'all' && (
                    <p className="text-xs text-green-600 mt-1">
                      +{overview?.new_users || 0} new
                    </p>
                  )}
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-total-questions">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Questions</p>
                  <p className="text-3xl font-bold text-gray-900">{overview?.total_questions || 0}</p>
                  <p className="text-xs text-gray-400 mt-1">In question bank</p>
                </div>
                <div className="h-12 w-12 bg-pink-100 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-pink-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-total-notes">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Published Notes</p>
                  <p className="text-3xl font-bold text-gray-900">{overview?.total_notes || 0}</p>
                  {overview?.pending_reviews > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      {overview.pending_reviews} pending review
                    </p>
                  )}
                </div>
                <div className="h-12 w-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-total-views">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Views</p>
                  <p className="text-3xl font-bold text-gray-900">{overview?.total_views || 0}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {overview?.unique_visitors || 0} unique visitors
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Eye className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different analytics sections */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-flex">
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">
              <FileText className="w-4 h-4 mr-2" />
              Content
            </TabsTrigger>
            <TabsTrigger value="forum" data-testid="tab-forum">
              <MessageCircle className="w-4 h-4 mr-2" />
              Forum
            </TabsTrigger>
            <TabsTrigger value="live-classes" data-testid="tab-live-classes">
              <Video className="w-4 h-4 mr-2" />
              Live Classes
            </TabsTrigger>
            <TabsTrigger value="engagement" data-testid="tab-engagement">
              <TrendingUp className="w-4 h-4 mr-2" />
              Engagement
            </TabsTrigger>
            <TabsTrigger value="flags" data-testid="tab-flags">
              <Flag className="w-4 h-4 mr-2" />
              Flags & Reports
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Users by Grade */}
              <Card data-testid="chart-users-by-grade">
                <CardHeader>
                  <CardTitle className="text-lg">Users by Grade</CardTitle>
                  <CardDescription>Distribution of registered users across grades</CardDescription>
                </CardHeader>
                <CardContent>
                  {usersByGrade.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={usersByGrade} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="grade" type="category" width={100} tick={{ fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Users" radius={[0, 4, 4, 0]}>
                          {usersByGrade.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={GRADE_COLORS[entry.grade] || CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Users by MMD */}
              <Card data-testid="chart-users-by-mmd">
                <CardHeader>
                  <CardTitle className="text-lg">Users by MMD Location</CardTitle>
                  <CardDescription>Distribution of users across MMD regions</CardDescription>
                </CardHeader>
                <CardContent>
                  {usersByMMD.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={usersByMMD}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ mmd, percent }) => `${mmd} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                          nameKey="mmd"
                        >
                          {usersByMMD.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No MMD data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Signups Trend */}
              <Card className="md:col-span-2" data-testid="chart-signups-trend">
                <CardHeader>
                  <CardTitle className="text-lg">User Signups Trend</CardTitle>
                  <CardDescription>New user registrations over time</CardDescription>
                </CardHeader>
                <CardContent>
                  {signupsTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={signupsTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="count"
                          name="Signups"
                          stroke="#8B5CF6"
                          fill="#8B5CF6"
                          fillOpacity={0.3}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No signup data for this period
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-6">
            {/* Content Summary Cards */}
            {contentSummary && (
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-2">Questions</p>
                      <p className="text-3xl font-bold">{contentSummary.questions.total}</p>
                      <div className="flex justify-center gap-4 mt-2 text-xs">
                        <span className="text-purple-600">Oral: {contentSummary.questions.oral}</span>
                        <span className="text-pink-600">Written: {contentSummary.questions.written}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-2">Notes/Chapters</p>
                      <p className="text-3xl font-bold">{contentSummary.notes.total}</p>
                      <div className="flex justify-center gap-2 mt-2 text-xs flex-wrap">
                        <Badge variant="outline" className="bg-green-50 text-green-700">{contentSummary.notes.published} Published</Badge>
                        <Badge variant="outline" className="bg-amber-50 text-amber-700">{contentSummary.notes.pending_review} Pending</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-2">Functions</p>
                      <p className="text-3xl font-bold">{contentSummary.functions.total}</p>
                      <div className="flex justify-center gap-4 mt-2 text-xs">
                        <span className="text-purple-600">Oral: {contentSummary.functions.oral}</span>
                        <span className="text-pink-600">Written: {contentSummary.functions.written}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {/* Questions by Grade */}
              <Card data-testid="chart-questions-by-grade">
                <CardHeader>
                  <CardTitle className="text-lg">Questions by Grade</CardTitle>
                  <CardDescription>Distribution of questions across grades</CardDescription>
                </CardHeader>
                <CardContent>
                  {questionsByGrade.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={questionsByGrade}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="grade" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Questions" fill="#EC4899" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notes by Grade */}
              <Card data-testid="chart-notes-by-grade">
                <CardHeader>
                  <CardTitle className="text-lg">Notes by Grade</CardTitle>
                  <CardDescription>Distribution of published notes across grades</CardDescription>
                </CardHeader>
                <CardContent>
                  {notesByGrade.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={notesByGrade}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="grade" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Notes" fill="#14B8A6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Forum Tab */}
          <TabsContent value="forum" className="space-y-6">
            {/* Forum Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Questions</p>
                      <p className="text-3xl font-bold text-gray-900">{forumOverview?.total_questions || 0}</p>
                      {period !== 'all' && (
                        <p className="text-xs text-green-600 mt-1">
                          +{forumOverview?.new_questions || 0} new
                        </p>
                      )}
                    </div>
                    <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Answers</p>
                      <p className="text-3xl font-bold text-gray-900">{forumOverview?.total_answers || 0}</p>
                      <p className="text-xs text-green-600 mt-1">
                        {forumOverview?.verified_answers || 0} verified
                      </p>
                    </div>
                    <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Pending Verification</p>
                      <p className={`text-3xl font-bold ${(forumOverview?.pending_verification || 0) > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                        {forumOverview?.pending_verification || 0}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">Answers awaiting review</p>
                    </div>
                    <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Avg Response Time</p>
                      <p className="text-3xl font-bold text-gray-900">
                        {forumResponseTime?.avg_response_hours || 0}h
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {forumResponseTime?.questions_analyzed || 0} questions analyzed
                      </p>
                    </div>
                    <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Status Distribution & Activity Trend */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Questions by Status */}
              <Card data-testid="chart-forum-by-status">
                <CardHeader>
                  <CardTitle className="text-lg">Questions by Status</CardTitle>
                  <CardDescription>Distribution of forum questions by status</CardDescription>
                </CardHeader>
                <CardContent>
                  {forumByStatus.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={forumByStatus}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ status, percent }) => `${status} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                          nameKey="status"
                        >
                          <Cell fill="#3B82F6" /> {/* Open - Blue */}
                          <Cell fill="#F59E0B" /> {/* Answered - Amber */}
                          <Cell fill="#10B981" /> {/* Verified - Green */}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No forum data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Forum Activity Trend */}
              <Card data-testid="chart-forum-activity">
                <CardHeader>
                  <CardTitle className="text-lg">Forum Activity Trend</CardTitle>
                  <CardDescription>Questions and answers over time</CardDescription>
                </CardHeader>
                <CardContent>
                  {forumActivityTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={forumActivityTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="questions"
                          name="Questions"
                          stroke="#3B82F6"
                          fill="#3B82F6"
                          fillOpacity={0.3}
                        />
                        <Area
                          type="monotone"
                          dataKey="answers"
                          name="Answers"
                          stroke="#10B981"
                          fill="#10B981"
                          fillOpacity={0.3}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No activity data for this period
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Questions by Grade & Function */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Questions by Grade */}
              <Card data-testid="chart-forum-by-grade">
                <CardHeader>
                  <CardTitle className="text-lg">Forum Questions by Grade</CardTitle>
                  <CardDescription>Distribution across certification grades</CardDescription>
                </CardHeader>
                <CardContent>
                  {forumByGrade.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={forumByGrade}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="grade" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Questions" fill="#8B5CF6" radius={[4, 4, 0, 0]}>
                          {forumByGrade.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={GRADE_COLORS[entry.grade] || CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Questions by Function */}
              <Card data-testid="chart-forum-by-function">
                <CardHeader>
                  <CardTitle className="text-lg">Top Functions (Questions)</CardTitle>
                  <CardDescription>Most discussed exam functions</CardDescription>
                </CardHeader>
                <CardContent>
                  {forumByFunction.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={forumByFunction} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="function" type="category" width={120} tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Questions" radius={[0, 4, 4, 0]}>
                          {forumByFunction.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No function data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Questions by MMD & Top Contributors */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Questions by MMD */}
              <Card data-testid="chart-forum-by-mmd">
                <CardHeader>
                  <CardTitle className="text-lg">Questions by MMD Location</CardTitle>
                  <CardDescription>Geographic distribution of questions</CardDescription>
                </CardHeader>
                <CardContent>
                  {forumByMmd.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={forumByMmd}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ mmd, percent }) => `${mmd?.split(' ')[0] || 'All'} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                          nameKey="mmd"
                        >
                          {forumByMmd.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No MMD data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Contributors */}
              <Card data-testid="table-forum-contributors">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-500" />
                    Top Contributors
                  </CardTitle>
                  <CardDescription>Users with most verified answers</CardDescription>
                </CardHeader>
                <CardContent>
                  {forumTopContributors.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead className="text-right">Verified</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {forumTopContributors.map((contributor, index) => (
                          <TableRow key={contributor.user_id || index}>
                            <TableCell className="font-medium">
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                            </TableCell>
                            <TableCell>{contributor.user_name || 'Unknown'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                contributor.user_role === 'admin' ? 'bg-red-50 text-red-700' :
                                contributor.user_role === 'sub_admin' ? 'bg-orange-50 text-orange-700' :
                                'bg-blue-50 text-blue-700'
                              }>
                                {contributor.user_role === 'admin' ? 'Admin' : 
                                 contributor.user_role === 'sub_admin' ? 'Mod' : 'User'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-semibold text-green-600">
                              {contributor.verified_answers}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-gray-400">
                      No contributor data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Forum Summary Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Forum Summary</CardTitle>
                <CardDescription>Key metrics at a glance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-sm text-blue-600 font-medium">Open</p>
                    <p className="text-2xl font-bold text-blue-700">{forumOverview?.open_questions || 0}</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-sm text-amber-600 font-medium">Answered</p>
                    <p className="text-2xl font-bold text-amber-700">{forumOverview?.answered_questions || 0}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-sm text-green-600 font-medium">Verified</p>
                    <p className="text-2xl font-bold text-green-700">{forumOverview?.verified_questions || 0}</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <p className="text-sm text-purple-600 font-medium">Contributors</p>
                    <p className="text-2xl font-bold text-purple-700">{forumOverview?.unique_contributors || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Live Classes Tab */}
          <TabsContent value="live-classes" className="space-y-6" data-testid="live-classes-analytics">
            {/* Row 1 — KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card data-testid="lc-kpi-total">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Total Classes</p>
                      <p className="text-2xl font-bold mt-1">{lcOverview?.total_classes ?? 0}</p>
                      <p className="text-xs text-gray-400 mt-1">{lcOverview?.this_month_classes ?? 0} this month</p>
                    </div>
                    <Calendar className="w-8 h-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="lc-kpi-completed">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Completed</p>
                      <p className="text-2xl font-bold mt-1">{lcOverview?.by_status?.completed ?? 0}</p>
                      <p className="text-xs text-gray-400 mt-1">{(lcOverview?.by_status?.scheduled ?? 0) + (lcOverview?.by_status?.live ?? 0)} upcoming</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-emerald-500" />
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="lc-kpi-attendees">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Unique Attendees</p>
                      <p className="text-2xl font-bold mt-1">{lcOverview?.unique_attendees ?? 0}</p>
                      <p className="text-xs text-gray-400 mt-1">across all reports</p>
                    </div>
                    <UserCheck className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="lc-kpi-hours">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Teaching Hours</p>
                      <p className="text-2xl font-bold mt-1">{lcOverview?.total_teaching_hours ?? 0}</p>
                      <p className="text-xs text-gray-400 mt-1">delivered</p>
                    </div>
                    <Clock className="w-8 h-8 text-amber-500" />
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="lc-kpi-attendance-rate">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Attendance Rate</p>
                      <p className="text-2xl font-bold mt-1">{lcOverview?.avg_attendance_rate_pct ?? 0}%</p>
                      <p className="text-xs text-gray-400 mt-1">registered → joined</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-pink-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Row 2 — Activity Trend */}
            <Card data-testid="lc-chart-activity">
              <CardHeader>
                <CardTitle className="text-lg">Class Activity Trend</CardTitle>
                <CardDescription>Classes conducted per week, with weekly unique attendees</CardDescription>
              </CardHeader>
              <CardContent>
                {lcActivityTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={lcActivityTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="classes" name="Classes" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.25} />
                      <Area yAxisId="right" type="monotone" dataKey="attendees" name="Attendees" stroke="#EC4899" fill="#EC4899" fillOpacity={0.25} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-gray-400">
                    No classes in this window yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Row 3 — By Grade + By MMD */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card data-testid="lc-chart-by-grade">
                <CardHeader>
                  <CardTitle className="text-lg">Classes by Grade</CardTitle>
                  <CardDescription>Distribution across MEO grades (multi-count if class targets multiple)</CardDescription>
                </CardHeader>
                <CardContent>
                  {lcByGrade.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={lcByGrade}
                          dataKey="count"
                          nameKey="grade"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={(e) => `${e.grade} (${e.count})`}
                        >
                          {lcByGrade.map((entry, i) => (
                            <Cell key={`c-${i}`} fill={GRADE_COLORS[entry.grade] || CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">No data</div>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="lc-chart-by-mmd">
                <CardHeader>
                  <CardTitle className="text-lg">Classes by MMD</CardTitle>
                  <CardDescription>MMD targeting — &ldquo;All MMDs&rdquo; means no location filter</CardDescription>
                </CardHeader>
                <CardContent>
                  {lcByMmd.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={lcByMmd} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="mmd" type="category" width={110} tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Classes" radius={[0, 4, 4, 0]}>
                          {lcByMmd.map((_e, i) => (
                            <Cell key={`m-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">No data</div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Row 4 — Top Hosts */}
            <Card data-testid="lc-chart-top-hosts">
              <CardHeader>
                <CardTitle className="text-lg">Top Hosts</CardTitle>
                <CardDescription>Ranked by number of classes hosted; minutes taught counted on completed classes</CardDescription>
              </CardHeader>
              <CardContent>
                {lcTopHosts.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(300, lcTopHosts.length * 38)}>
                    <BarChart data={lcTopHosts} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="host_name" type="category" width={140} tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="classes_hosted" name="Classes" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="minutes_taught" name="Minutes taught" fill="#14B8A6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-gray-400">No hosts on record</div>
                )}
              </CardContent>
            </Card>

            {/* Row 5 — Attendance Summary Table */}
            <Card data-testid="lc-attendance-table">
              <CardHeader>
                <CardTitle className="text-lg">Attendance Summary</CardTitle>
                <CardDescription>Per-class attendance from cached Zoom reports. Row click → drilldown in Admin → Live Classes.</CardDescription>
              </CardHeader>
              <CardContent>
                {lcAttendance.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Class</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Host</TableHead>
                          <TableHead className="text-right">Registered</TableHead>
                          <TableHead className="text-right">Joined</TableHead>
                          <TableHead className="text-right">No-shows</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lcAttendance.map((row) => (
                          <TableRow
                            key={row.class_id}
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => navigate(`/admin/live-classes?class=${row.class_id}`)}
                            data-testid={`lc-attendance-row-${row.class_id}`}
                          >
                            <TableCell className="font-medium max-w-xs truncate">{row.title}</TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {row.start_time ? new Date(row.start_time).toLocaleDateString() : '—'}
                            </TableCell>
                            <TableCell className="text-sm">{row.host_name || '—'}</TableCell>
                            <TableCell className="text-right">{row.registered}</TableCell>
                            <TableCell className="text-right">{row.joined}</TableCell>
                            <TableCell className="text-right">{row.no_shows}</TableCell>
                            <TableCell className="text-right">
                              {row.attendance_rate_pct !== null ? (
                                <Badge variant={row.attendance_rate_pct >= 60 ? 'default' : 'secondary'}>
                                  {row.attendance_rate_pct}%
                                </Badge>
                              ) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-gray-400">
                    Attendance data will appear once completed classes have their Zoom reports fetched
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Row 6 — Cancellation stats */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card data-testid="lc-cancellation-rate">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Cancellation Rate</p>
                      <p className="text-3xl font-bold mt-1">{lcCancellation?.cancellation_rate_pct ?? 0}%</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {lcCancellation?.cancelled_classes ?? 0} of {lcCancellation?.total_classes ?? 0} classes
                      </p>
                    </div>
                    <CancelIcon className="w-10 h-10 text-red-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="md:col-span-2" data-testid="lc-cancellation-reasons">
                <CardHeader>
                  <CardTitle className="text-base">Top Cancellation Reasons</CardTitle>
                  <CardDescription>Verbatim reasons from cancelled classes, top 5</CardDescription>
                </CardHeader>
                <CardContent>
                  {lcCancellation?.top_reasons?.length > 0 ? (
                    <ul className="space-y-2">
                      {lcCancellation.top_reasons.map((r, i) => (
                        <li key={i} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                          <span className="text-gray-700">{r.reason}</span>
                          <Badge variant="secondary">{r.count}</Badge>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="h-[120px] flex items-center justify-center text-gray-400">
                      No cancellations recorded
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>


          {/* Engagement Tab */}
          <TabsContent value="engagement" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Activity Trend */}
              <Card className="md:col-span-2" data-testid="chart-activity-trend">
                <CardHeader>
                  <CardTitle className="text-lg">User Activity Trend</CardTitle>
                  <CardDescription>Daily user activity (note views, question progress)</CardDescription>
                </CardHeader>
                <CardContent>
                  {activityTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={activityTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Line
                          type="monotone"
                          dataKey="activities"
                          name="Activities"
                          stroke="#3B82F6"
                          strokeWidth={2}
                          dot={{ fill: '#3B82F6', strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No activity data for this period
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Viewed Notes */}
              <Card className="md:col-span-2" data-testid="table-top-viewed-notes">
                <CardHeader>
                  <CardTitle className="text-lg">Most Viewed Notes</CardTitle>
                  <CardDescription>Top 10 most viewed study notes</CardDescription>
                </CardHeader>
                <CardContent>
                  {topViewedNotes.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Grade</TableHead>
                          <TableHead className="text-right">Views</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {topViewedNotes.map((note, index) => (
                          <TableRow key={note.id || index}>
                            <TableCell className="font-medium">{index + 1}</TableCell>
                            <TableCell>{note.title || 'Untitled'}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{note.grade || 'N/A'}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="flex items-center justify-end gap-1">
                                <Eye className="w-4 h-4 text-gray-400" />
                                {note.view_count}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-gray-400">
                      No view data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Flags & Reports Tab */}
          <TabsContent value="flags" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pending Flags</p>
                      <p className="text-2xl font-bold">{overview?.pending_flags || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pending Reviews</p>
                      <p className="text-2xl font-bold">{overview?.pending_reviews || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Flag Reasons Chart */}
              <Card data-testid="chart-flag-reasons">
                <CardHeader>
                  <CardTitle className="text-lg">Flag Reasons Distribution</CardTitle>
                  <CardDescription>Why users are reporting content</CardDescription>
                </CardHeader>
                <CardContent>
                  {flagReasons.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={flagReasons}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ reason, percent }) => `${reason.split(' ')[0]} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                          nameKey="reason"
                        >
                          {flagReasons.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No flags reported yet
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Flag Reasons Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Flag Reasons Breakdown</CardTitle>
                  <CardDescription>Detailed count by reason type</CardDescription>
                </CardHeader>
                <CardContent>
                  {flagReasons.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Reason</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {flagReasons.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                                />
                                {item.reason}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-medium">{item.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-gray-400">
                      No flags reported yet
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AnalyticsPage;
